import React, { useContext, useEffect } from "react";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { SettingsSectionContext, useSettingsPage } from "./SettingsPageContext";

type Props = {
    /** DOM id and registry id, e.g. "orders". */
    id: string;
    titleKey: string;
    descriptionKey?: string;
    /** Font Awesome class, e.g. "fad fa-clipboard-list". */
    iconClass: string;
    /** Translation key of the group heading in the section menu (company, selling, ...). */
    groupKey: string;
    /**
     * For a module section: the hero card holding the master toggle. Rendered above the cards,
     * and alone (with `collapsedMessage`) while `collapsed` is true.
     */
    hero?: React.ReactNode;
    collapsed?: boolean;
    collapsedMessage?: React.ReactNode;
    children?: React.ReactNode;
};

/**
 * One top-level section of the settings page: heading with icon and description, then cards.
 * Registers itself for the sticky header's section menu and scroll-spy.
 */
export const SettingsSection = (props: Props) => {
    const page = useSettingsPage();
    const { registerSection } = page;

    // Prefixed so the section never shares an id with the app's side navigation links
    // ("orders", "inventory", ...). Callers and tests keep using the short name.
    const domId = `settings-${props.id}`;

    useEffect(() => {
        return registerSection({ id: domId, titleKey: props.titleKey, iconClass: props.iconClass, groupKey: props.groupKey });
    }, [registerSection, domId, props.titleKey, props.iconClass, props.groupKey]);

    const sectionContextValue = React.useMemo(() => ({ id: domId, titleKey: props.titleKey }), [domId, props.titleKey]);

    return (
        <SettingsSectionContext.Provider value={sectionContextValue}>
            <section className={"SettingsSection" + (props.collapsed ? " collapsed" : "")} id={domId} data-testid={`section-${props.id}`}>
                <div className={"SettingsSection-head"}>
                    <div className={"SettingsSection-icon"}>
                        <i className={props.iconClass} aria-hidden={true} />
                    </div>
                    <div>
                        <h2 className={"SettingsSection-title"}>
                            <OwLabel title={props.titleKey} noBottomMargin={true} />
                        </h2>
                        {props.descriptionKey ? (
                            <p className={"SettingsSection-description"}>
                                <OwLabel title={props.descriptionKey} noBottomMargin={true} />
                            </p>
                        ) : null}
                    </div>
                </div>

                {props.hero}

                {props.collapsed ? (
                    props.collapsedMessage ? (
                        <div className={"SettingsSection-collapsed"}>
                            <i className={"fad fa-circle-info"} aria-hidden={true} />
                            <span>{props.collapsedMessage}</span>
                        </div>
                    ) : null
                ) : (
                    props.children
                )}
            </section>
        </SettingsSectionContext.Provider>
    );
};

export const useSettingsSection = () => useContext(SettingsSectionContext);

export default SettingsSection;
